// Real-time sync over WebSocket. One change is in flight at a time; later local changes wait in a buffer.
// Remote changes are transformed over our unconfirmed ones (and vice versa) so every browser converges
// to the order the server chose. Each change carries this tab's own increasing number: after a dropped connection the
// server says which number it stored last, so a change that got through is never sent (and applied) twice.
import { CLIENT_MAX_OPS, addedSheets, remapSheetOp, transform } from '../../shared/ops.ts';
import type { Op } from '../../shared/ops.ts';
import type { DocModel } from './model.ts';

export interface SyncEvents {
  snapshot(msg: any): void;
  remote(op: Op, by: number): void;
  presence(users: { id: number; name: string; email?: string; sel: any }[]): void;
  status(state: 'connecting' | 'online' | 'offline' | 'saving' | 'saved'): void;
  message(msg: any): void;     // comments / title / role / revoked notifications
  /** A sheet this browser added now has another id (see remapSheetOp). */
  remap(from: number, to: number): void;
  /** A sheet this browser added now has another name (someone added one of that name at the same moment). */
  renamed(sheet: number, name: string): void;
}

/** Keeps every message far below the server's 16 MB limit. */
const MAX_MESSAGE = 4 * 1024 * 1024;

export class Sync {
  seq = 0;
  private ws: WebSocket | null = null;
  private inflight: { id: number; op: Op } | null = null;
  private buffer: Op[] = [];
  private counter = 0;
  /** This tab's id: with `counter` it numbers the changes sent from here. */
  private tab = Array.from(crypto.getRandomValues(new Uint8Array(12)), b => b.toString(16).padStart(2, '0')).join('');
  private retry = 0;
  /** How often the change in flight was answered with "busy" (the database could not store it just then). */
  private busy = 0;
  /**
   * Sheets moved here before the server had its say: the change adding them was already on its way when someone
   * else's sheet with the same id arrived. Server id -> the id used here meanwhile; settled by the ack (`remap`).
   */
  private provisional = new Map<number, number>();
  private closed = false;
  /** Set by resume(): the server is told this browser already holds the document as of that change. */
  private have: number | null = null;
  private docId: string;
  private model: DocModel;
  private ev: SyncEvents;

  constructor(docId: string, model: DocModel, ev: SyncEvents) { this.docId = docId; this.model = model; this.ev = ev; }

  connect(): void {
    if (this.closed) return;
    this.ev.status('connecting');
    const ws = new WebSocket(`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws?doc=${encodeURIComponent(this.docId)}&tab=${this.tab}${this.have ? `&have=${this.have}` : ''}`);
    this.ws = ws;
    ws.onmessage = e => { if (this.ws === ws && !this.closed) this.onMessage(JSON.parse(e.data)); };
    ws.onopen = () => { if (this.ws === ws) this.retry = 0; };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.closed) return;
      this.ev.status('offline');
      setTimeout(() => { if (!this.closed && !this.ws) this.connect(); }, Math.min(30_000, 500 * 2 ** this.retry++));
    };
  }
  close(): void { this.closed = true; const ws = this.ws; this.ws = null; ws?.close(); }
  /** Go live again after an import this browser showed itself: from change `seq` on, without reloading the document. */
  resume(seq: number): void { this.closed = false; this.seq = seq; this.have = seq; this.inflight = null; this.buffer = []; this.connect(); }
  get pending(): number { return (this.inflight ? 1 : 0) + this.buffer.length; }
  /** Resolves when every queued change is confirmed, or after `ms` (before the page is left or reloaded). */
  drain(ms: number): Promise<void> {
    return new Promise(res => {
      const t0 = Date.now();
      const tick = (): void => { if (!this.pending || Date.now() - t0 > ms) res(); else setTimeout(tick, 100); };
      this.flush(); tick();
    });
  }
  /** The ids of sheets added by changes the server has not confirmed yet. */
  private unconfirmedSheets(): Set<number> { return new Set([...(this.inflight ? addedSheets(this.inflight.op) : []), ...this.buffer.flatMap(addedSheets)]); }
  /** Give sheet `id` (added here, not yet confirmed) the name `name`: the model, its unsent sheet.add, the editor. */
  private rename(id: number, name: string): void {
    const s = this.model.sheet(id);
    if (!s || s.name === name) return;
    this.model.apply({ t: 'sheet.rename', sheet: id, name });
    const fix = (o: Op): Op => (o.t === 'batch' ? { t: 'batch', ops: o.ops.map(fix) } : o.t === 'sheet.add' && o.sheet === id ? { ...o, name } : o);
    if (this.inflight) this.inflight.op = fix(this.inflight.op);
    this.buffer = this.buffer.map(fix);
    this.ev.renamed(id, name);
  }
  /** A name no sheet here has (case does not matter), from `name` and a counter. */
  private freeName(name: string): string {
    const has = (n: string): boolean => this.model.sheets.some(s => s.name.toLowerCase() === n.toLowerCase());
    let n = name, k = 2; while (has(n)) n = `${name} (${k++})`;
    return n;
  }
  /** Move sheet `from` (added here, not yet confirmed) to id `to`: the model, the unsent changes, the editor. */
  private remap(from: number, to: number): void {
    if (from === to) return;
    this.model.renumberSheet(from, to);
    if (this.inflight) this.inflight.op = remapSheetOp(this.inflight.op, from, to);
    this.buffer = this.buffer.map(o => remapSheetOp(o, from, to));
    this.ev.remap(from, to);
  }

  /** Apply a local change immediately and queue it for the server. */
  local(op: Op): void {
    this.model.apply(op);
    this.buffer.push(op);
    this.flush();
  }
  select(sel: unknown): void { if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: 'sel', sel })); }
  private freeSheetId(): number {
    for (;;) { const id = 100_000 + Math.floor(Math.random() * 900_000); if (!this.model.sheet(id)) return id; }
  }

  private flush(): void {
    if (this.inflight || !this.buffer.length || this.ws?.readyState !== WebSocket.OPEN) { if (this.pending) this.ev.status('saving'); return; }
    // queued changes go out as one flat batch (the server refuses nested batches), at most 20,000 changes and 4 MB per
    // message; a bigger change (a huge paste) is split over several messages
    const flat: Op[] = [];
    let bytes = 0;
    // batches inside batches (redo of an undone batch is one) are spread out too
    const spread = (o: Op): Op[] => (o.t === 'batch' ? o.ops.flatMap(spread) : [o]);
    while (this.buffer.length) {
      const ops = spread(this.buffer[0]);
      let taken = 0;
      for (const o of ops) {
        const size = JSON.stringify(o).length;
        if (flat.length && (flat.length >= CLIENT_MAX_OPS || bytes + size > MAX_MESSAGE)) break;
        flat.push(o); bytes += size; taken++;
      }
      if (taken < ops.length) { const rest = ops.slice(taken); this.buffer[0] = rest.length === 1 ? rest[0] : { t: 'batch', ops: rest }; break; }
      this.buffer.shift();
    }
    const op: Op = flat.length === 1 ? flat[0] : { t: 'batch', ops: flat };
    this.inflight = { id: ++this.counter, op };
    this.send();
  }
  private send(): void {
    if (!this.inflight || this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify({ t: 'op', id: this.inflight.id, base: this.seq, op: this.inflight.op }));
    this.ev.status('saving');
  }

  private onMessage(msg: any): void {
    switch (msg.t) {
      case 'snapshot': {
        this.have = null;
        // the change in flight when the connection dropped may have been stored: then the server's state has it
        if (this.inflight && typeof msg.last === 'number' && this.inflight.id <= msg.last) this.inflight = null;
        // the server agrees this browser is up to date (after resume): nothing to load
        if (msg.light) { this.seq = msg.seq; this.ev.snapshot(msg); this.ev.status(this.pending ? 'saving' : 'online'); if (this.inflight) this.send(); else this.flush(); return; }
        // (re)connected: start from the server's state, then replay what we had not got confirmed; the change that was
        // in flight goes again with its own number
        this.seq = msg.seq;
        this.ev.snapshot(msg);
        if (this.inflight) this.model.apply(this.inflight.op);
        for (const op of this.buffer) this.model.apply(op);
        this.ev.status(this.pending ? 'saving' : 'online');
        if (this.inflight) this.send(); else this.flush();
        return;
      }
      case 'ack':
        if (!this.inflight || this.inflight.id !== msg.id) return;
        this.seq = msg.seq; this.inflight = null; this.busy = 0;
        // a sheet added here got another id or name on the server (someone added one at the same moment); a sheet
        // moved here meanwhile goes where the server put it (or back, when the server kept the id after all)
        for (const [from, to] of Object.entries((msg.remap ?? {}) as Record<string, number>)) { const f = Number(from); this.remap(this.provisional.get(f) ?? f, to); this.provisional.delete(f); }
        for (const [serverId, local] of this.provisional) this.remap(local, serverId);
        this.provisional.clear();
        if (msg.renames) for (const [id, name] of Object.entries(msg.renames as Record<string, string>)) this.rename(Number(id), name);
        this.ev.status(this.buffer.length ? 'saving' : 'saved');
        this.flush();
        return;
      case 'busy':
        // the server could not store it just now (database busy): the same change again a little later, ten times at most
        if (!this.inflight || this.inflight.id !== msg.id) return;
        if (++this.busy > 10) { this.onMessage({ t: 'reject', id: msg.id, error: 'server busy' }); return; }
        setTimeout(() => this.send(), Math.min(15_000, 1000 * 2 ** (this.busy - 1)));
        return;
      case 'reject':
        // the server refused our change: drop local state and reload from the server
        console.warn('change rejected:', msg.error);
        this.inflight = null; this.buffer = [];
        this.ws?.close();
        this.ev.message({ t: 'rejected', error: msg.error });
        return;
      case 'op': {
        if (msg.seq <= this.seq) return;
        this.seq = msg.seq;
        let remote: Op | null = msg.op;
        // someone else's new sheet has the id a sheet added here (not yet confirmed) chose: ours moves to a number
        // nobody picks by counting, and the server keeps it unless that one is taken too (then the ack says where it went)
        if (remote) {
          const sent = new Set(this.inflight ? addedSheets(this.inflight.op) : []);
          for (const id of addedSheets(remote)) if (this.unconfirmedSheets().has(id)) {
            const local = this.freeSheetId();
            if (sent.has(id)) this.provisional.set(id, local);   // the server will say where this sheet really went
            this.remap(id, local);
          }
          // ... and the name: formulas tell sheets apart by it, so two sheets never share one
          const mine = this.unconfirmedSheets();
          const named = (o: Op): string[] => (o.t === 'batch' ? o.ops.flatMap(named) : o.t === 'sheet.add' || o.t === 'sheet.rename' ? [o.name.toLowerCase()] : []);
          for (const n of named(remote)) for (const s of this.model.sheets) if (mine.has(s.id) && s.name.toLowerCase() === n) this.rename(s.id, this.freeName(s.name));
        }
        // our unconfirmed changes come after the remote one on the server: shift them over it,
        // and shift the remote one over them (remote first) before applying it locally
        if (this.inflight) {
          const r2 = remote && transform(remote, this.inflight.op, true);
          const mine = remote ? transform(this.inflight.op, remote) : this.inflight.op;
          remote = r2;
          if (mine) this.inflight.op = mine;
        }
        this.buffer = this.buffer.map(b => {
          if (!remote) return b;
          const r2 = transform(remote, b, true), mine = transform(b, remote);
          remote = r2; return mine;
        }).filter((b): b is Op => b !== null);
        if (remote) { this.model.apply(remote); this.ev.remote(remote, msg.by); }
        return;
      }
      case 'presence': this.ev.presence(msg.users); return;
      default: this.ev.message(msg);
    }
  }
}

/**
 * The change a server `reload` names when it follows someone's import, for reloadAfterReceipt; Infinity when the server
 * did not say which (the import is newer than anything shown). Null for any other reload (a restored version).
 */
export function importReloadSeq(msg: { t?: unknown; reason?: unknown; seq?: unknown }): number | null {
  if (msg.t !== 'reload' || msg.reason !== 'import') return null;
  return Number.isSafeInteger(msg.seq) ? msg.seq as number : Infinity;
}

/**
 * After the server confirmed an import of ours committed as change `seq`, while this page may still show an older
 * revision: reload so it opens the import, but only once nothing typed here is unsent. A drain() that returns is no
 * proof of that (it gives up after its wait), so the queue is checked again at the very moment of reloading and,
 * while anything is pending, the page keeps its changes and waits on. It never reloads when the page already shows
 * that change (`page.shown()`), nor once `page.still()` says another import or kept file has come along since.
 */
export async function reloadAfterReceipt(sync: Pick<Sync, 'pending' | 'drain'>, page: { shown(): number; still(): boolean },
  seq: number, reload: () => void, waiting?: () => void, ms = 3000): Promise<'current' | 'superseded' | 'reloaded'> {
  let told = false;
  for (;;) {
    if (seq <= page.shown()) return 'current';
    if (!page.still()) return 'superseded';
    if (!sync.pending) { reload(); return 'reloaded'; }
    if (!told) { told = true; waiting?.(); }
    await sync.drain(ms);
  }
}
